/* eslint-disable no-nested-ternary */
import { useDispatch } from "react-redux";
import React, { ReactNode, useState, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import InfoIcon from "@mui/icons-material/Info";

import Panel from "../../../components/Panel";
import { tl } from "../../../components/translate";
import SalaryBreakdown from "./Components/SalaryBreakdown";
import SelectedEmployee from "./Components/SelectedEmployee";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import useMobileScreen from "../../../hooks/useMobileScreen";
import { Employee, UserMode } from "../../../interfaces/User";
import { notificationAdd } from "../../../actions/notification";
import useRcUsesCitPfSsf from "../../../hooks/useRcUsesCitPfSsf";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import {
  usePostSalary,
  usePatchSalary,
  useCalculateEmployeePayrollSummary
} from "../../../hooks/query/useSalaries";
import EmployeeAndSpSelect from "../../../components/Employee/EmployeeAndSpSelect";

import {
  TRetirementFundType,
  RetirementFundType,
  PayrollAttendanceSource,
  calendarSummaryExtraData
} from "../../../constants/payroll";

import {
  convertADtoBS,
  getNepaliMonths
} from "../../../components/Calendar/functions/calendarFunctions";

import {
  BonusComponents,
  DeductionsComponents,
  MonthlyAllowanceComponents
} from "../../../config/payroll";

import {
  TSalarySummary,
  TEarningDeductionComponent,
  TSalary,
  TSalaryFormData,
  TCalendarSummary,
  TRetirementFundContributionBreakdown,
  TLeaveSummary
} from "../../../interfaces/SalaryInterface";
import { useDebouncedCallback } from "../../../hooks/useDebouncedCallback";
import { CalendarFunctions } from "../../../components/Calendar";

const AllMonthlyAllowanceComponents = [
  MonthlyAllowanceComponents.DEARNESS_ALLOWANCE,
  MonthlyAllowanceComponents.HOUSE_RENT_ALLOWANCE,
  MonthlyAllowanceComponents.CONVEYANCE_ALLOWANCE,
  MonthlyAllowanceComponents.MEDICAL_ALLOWANCE,
  MonthlyAllowanceComponents.OTHER_ALLOWANCE
];

const AllBonuseFields = [BonusComponents.FESTIVAL_BONUS, BonusComponents.OTHER_BONUS];
const AllDeductionsComponents = [DeductionsComponents.OTHER_DEDUCTIONS];

export const defaultFundContribution: TRetirementFundContributionBreakdown = {
  employee: 0,
  employer: 0
};

export function getPfSsfLabel({ pf, ssf }: { pf: boolean; ssf: boolean; cit?: boolean }): string {
  if (pf) return "PF";
  if (ssf) return "SSF";
  return "";
}

/**
 * @info salary is for salary that is disbursed i.e. gotten from db
 * @info salatFormData is for salary that is not yet disbursed but attached
 * to employee for bulk payroll rull
 */
interface PropTypes {
  onClose: () => void;
  salary?: Partial<TSalary> | null;
  onCreateSuccess?: (data: TSalary) => void;
  onUpdateSuccess?: (data: TSalary) => void;
}

export function SalaryCreateEdit({
  salary,
  onClose,
  onCreateSuccess,
  onUpdateSuccess
}: PropTypes): ReactNode {
  const isEdit = !!salary?.id;

  // hooks
  const dispatch = useDispatch();
  const isMobile = useMobileScreen();
  const rc = useCurrentResourceCentre();
  const { rcUsesProvidentFund, rcUsesSocialSecurityFund } = useRcUsesCitPfSsf();
  const { mutate: postSalaryMutation, isLoading: isPosting } = usePostSalary();
  const { mutate: patchSalaryMutation, isLoading: isPatching } = usePatchSalary();
  const { mutate: calculateEmployeePayrollSummary } = useCalculateEmployeePayrollSummary();

  // values
  const attendanceSource =
    rc?.settings?.payrollSettings?.attendanceSource ?? PayrollAttendanceSource.SYSTEM;

  const loading = isPosting || isPatching;

  // Salary Month and Year (Nepali BS) and Status states
  const currentBs = convertADtoBS(new Date());
  const currentBsYear = currentBs.bsYear;
  const currentBsMonth = currentBs.bsMonth;

  const totalCalendarDays = CalendarFunctions.getBsMonthDays(currentBsYear, currentBsMonth);

  const nepaliYears = Array.from({ length: 5 }, (_, i) => currentBsYear - i);
  const nepaliMonths = getNepaliMonths();

  // states
  const [confirmCreate, setConfirmCreate] = useState<boolean>(false);
  const [confirmUpdate, setConfirmUpdate] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | ServiceProvider | null>(
    salary?.employee || salary?.serviceProvider || null
  );

  // states for form data
  const [taxAdjustment, setTaxAdjustment] = useState<number>(0);
  const [payrollMonth, setPayrollMonth] = useState<number>(salary?.payrollMonth || currentBsMonth);
  const [payrollYear, setPayrollYear] = useState<number>(salary?.payrollYear || currentBsYear);
  const [retirementFundType, setRetirementFundType] = useState<
    Exclude<TRetirementFundType, typeof RetirementFundType.CITIZEN_INVESTMENT_TRUST>
  >(
    salary?.retirementFundType ||
      (rcUsesProvidentFund
        ? RetirementFundType.PROVIDENT_FUND
        : rcUsesSocialSecurityFund
        ? RetirementFundType.SOCIAL_SECURITY_FUND
        : RetirementFundType.NONE)
  );

  // reimbursements
  const [reimbursements, setReimbursements] = useState({
    travel: { value: 0 },
    food: { value: 0 }
  });

  // states for amount fields
  const [basicSalary, setBasicSalary] = useState<number>(salary?.earnings?.basicSalary || 0);
  const [allowances, setAllowances] = useState<Record<string, TEarningDeductionComponent>>(
    salary?.earnings?.allowances || {}
  );
  const [bonuses, setBonuses] = useState<Record<string, TEarningDeductionComponent>>(
    salary?.earnings?.bonuses || {}
  );
  const [deductions, setDeductions] = useState<Record<string, TEarningDeductionComponent>>(
    salary?.deductions || {}
  );

  // states for summaries (calendar, leave, salary summary comes from api)
  const [leaveSummary, setLeaveSummary] = useState<TLeaveSummary>(salary?.leaveSummary ?? {});
  const [calendarSummary, setCalendarSummary] = useState<TCalendarSummary>(
    salary?.calendarSummary ?? {
      // NOTE: the order in which these keys has been placed matters in UI.
      // The derived values are kept later on the order.
      // So, dont change order.
      calendarDays: totalCalendarDays,
      employmentDays: 0,
      defaultWeeklyOffs: 0,
      publicHolidays: 0,
      unpaidLeaveDays: 0,
      businessDays: 0,
      payableDays: 0
    }
  );
  const [salarySummary, setSalarySummary] = useState<TSalarySummary>({
    salary: {
      basicProportionateSalary: 0,
      totalProportionateAllowance: 0,
      grossMonthlySalary: 0,
      netMonthlySalary: 0,
      totalPayable: 0
    },

    taxableIncome: {
      annualGrossTaxableIncome: 0,
      annualNetTaxableIncome: 0,
      pastTaxableIncomeTotal: 0,
      currentMonthTaxableIncome: 0,
      futureTaxableIncomeTotal: 0
    },

    tax: {
      grossAnnualTaxLiability: 0,
      femaleRebate: 0,
      netAnnualTaxLiability: 0,
      taxAdjustment: 0,
      netCurrentMonthTax: 0,
      taxBreakdown: []
    },

    retirementContributions: {
      annualRetirementContribution: 0,
      allowedAnnualRetirementContributionDeduction: 0,
      summary: {
        pfContributions: { employee: 0, employer: 0 },
        ssfContributions: { employee: 0, employer: 0 },
        employeePastRetirementContributionTotal: 0,
        employerPastRetirementContributionTotal: 0,
        employeeCurrentRetirementContribution: 0,
        employerCurrentRetirementContribution: 0,
        employeeFutureRetirementContributionTotal: 0,
        employerFutureRetirementContributionTotal: 0
      }
    }
  });

  // helper functions
  const isEmployee =
    selectedEmployee?.userAuthenticable?.[0]?.authenticable === UserMode.Employee ||
    Boolean(
      selectedEmployee && "nonUserEmployee" in selectedEmployee && selectedEmployee.nonUserEmployee
    );
  const isServiceProvider =
    selectedEmployee?.userAuthenticable?.[0]?.authenticable === UserMode.ServiceProvider;

  const buildFormData = (): TSalaryFormData => ({
    // employee details
    resourceCentreId: rc?.id as number,
    employeeId: isEmployee ? selectedEmployee?.id : undefined,
    serviceProviderId: isServiceProvider ? selectedEmployee?.id : undefined,
    payrollYear,
    payrollMonth,
    taxAdjustment,

    // attendance source
    attendanceSource,

    // calendar summary for custom attenddance source
    calendarSummary:
      attendanceSource === PayrollAttendanceSource.CUSTOM ? calendarSummary : undefined,

    // earnings
    earnings: {
      basicSalary,
      allowances: Object.entries(allowances)
        .filter(([, allowance]) => allowance?.value > 0)
        .reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: { value: value.value }
          }),
          {} as Record<string, { value: number }>
        ),

      bonuses: Object.entries(bonuses)
        .filter(([, bonus]) => bonus?.value > 0)
        .reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: { value: value.value }
          }),
          {} as Record<string, { value: number }>
        )
    },

    // deductions
    deductions: Object.entries(deductions)
      .filter(([, deduction]) => deduction?.value > 0)
      .reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: { value: value.value }
        }),
        {} as Record<string, { value: number }>
      ),

    // fund contribution data
    retirementFundType,

    // reimbursements
    reimbursements: Object.entries(reimbursements)
      .filter(([, item]) => item.value > 0)
      .reduce(
        (acc, [key, item]) => ({
          ...acc,
          [key]: { value: item.value }
        }),
        {} as Record<string, { value: number }>
      )
  });

  // handle create
  // eslint-disable-next-line consistent-return
  const handleCreate = () => {
    if (!selectedEmployee?.activeCtc) {
      return dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          message:
            "Selected employee has no CTC. Please create CTC for all the employees to run payroll.",
          autoTimeout: true
        })
      );
    }

    const formData = buildFormData();
    postSalaryMutation(formData, {
      onSuccess: (response) => {
        if (response.success) {
          onCreateSuccess?.(response.data);
          onClose();
        }
      }
    });
    setConfirmCreate(false);
  };

  // handle update
  const handleUpdate = () => {
    if (!salary?.id) return;

    const formData = buildFormData();
    patchSalaryMutation(
      { id: salary.id, data: formData },
      {
        onSuccess: (response) => {
          if (response.success) {
            onUpdateSuccess?.(response.data);
            onClose();
          }
        }
      }
    );
    setConfirmUpdate(false);
  };

  const debouncedCalculateSalarySummary = useDebouncedCallback(() => {
    if (basicSalary > 0) {
      calculateEmployeePayrollSummary(
        {
          employeeId: isEmployee ? selectedEmployee?.id : undefined,
          serviceProviderId: isServiceProvider ? selectedEmployee?.id : undefined,
          resourceCentreId: rc?.id,
          payrollYear,
          payrollMonth,
          earnings: {
            basicSalary,
            allowances,
            bonuses
          },
          deductions,
          taxAdjustment,
          attendanceSource,
          calendarSummary:
            attendanceSource === PayrollAttendanceSource.CUSTOM ? calendarSummary : undefined,
          reimbursements: Object.entries(reimbursements)
            .filter(([, item]) => item.value > 0)
            .reduce(
              (acc, [key, item]) => ({
                ...acc,
                [key]: { value: item.value }
              }),
              {} as Record<string, { value: number }>
            )
        },
        {
          onSuccess: (response) => {
            if (response.success && response.data) {
              setSalarySummary(response.data?.salarySummary);
              setLeaveSummary(response.data?.leaveSummary);
              if (attendanceSource === PayrollAttendanceSource.SYSTEM) {
                setCalendarSummary(response.data?.calendarSummary);
              }
            }
          }
        }
      );
    }
  }, 1000);

  // Recalculate for normal payroll changes
  useEffect(() => {
    if (!selectedEmployee?.activeCtc || !payrollYear || !payrollMonth) {
      return;
    }

    debouncedCalculateSalarySummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, bonuses, deductions, taxAdjustment, retirementFundType, reimbursements]);

  // Recalculate when user edits custom calendar summary
  useEffect(() => {
    if (
      attendanceSource !== PayrollAttendanceSource.CUSTOM ||
      !selectedEmployee?.activeCtc ||
      !payrollYear ||
      !payrollMonth
    ) {
      return;
    }

    debouncedCalculateSalarySummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarSummary]);

  // effects
  useEffect(() => {
    if (!isEdit && selectedEmployee) {
      setBasicSalary(selectedEmployee.activeCtc?.monthlyEarnings?.basicSalary || 0);
      setAllowances(selectedEmployee?.activeCtc?.monthlyEarnings?.allowances ?? {});
    }

    if (!isEdit && !selectedEmployee) {
      setBasicSalary(0);
      setAllowances({});
    }
  }, [selectedEmployee, isEdit, salary]);

  return (
    <Panel
      wrapperStyle={{
        zIndex: 1300,
        width: "calc(100vw - 80px)",
        maxWidth: "calc(100vw - 80px)"
      }}
      contentStyle={{
        overflowY: isMobile ? "auto" : "hidden",
        py: 0
      }}
      onClose={onClose}
      titleJsx={
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Typography>
            {!isEdit
              ? salary
                ? "Edit Salary Configuration"
                : "Disburse Salary"
              : "Edit Salary Disbursement"}
          </Typography>
        </Box>
      }
      footer={
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2
          }}
        >
          <Button
            size="small"
            onClick={onClose}
            variant="outlined"
            sx={{ height: "35px" }}
            disabled={loading}
          >
            {tl("global.cancel")}
          </Button>

          <Button
            size="small"
            variant="contained"
            sx={{ height: "35px" }}
            disabled={loading}
            data-testmation="runPayrollButton"
            onClick={() => {
              if (!isEdit) {
                setConfirmCreate(true);
              }

              if (isEdit && salary?.id) {
                setConfirmUpdate(true);
              }
            }}
          >
            {!isEdit ? tl("payroll.run") : tl("update")}
          </Button>
        </Box>
      }
    >
      {confirmCreate && (
        <OkhatiDialog
          title={tl("create")}
          description="Are you sure you want to create salary data? Please review the details before proceeding."
          next={handleCreate}
          cancel={() => setConfirmCreate(false)}
          readMode={false}
        />
      )}

      {confirmUpdate && (
        <OkhatiDialog
          title={tl("update")}
          description="Are you sure you want to update the salary data? Please review the details before proceeding."
          next={handleUpdate}
          cancel={() => setConfirmUpdate(false)}
          readMode={false}
        />
      )}

      <Box
        sx={{
          width: "100%",
          height: { xs: "auto", md: "100%" },
          display: "flex",
          flexDirection: {
            xs: "column",
            md: "row"
          },
          gap: 2
        }}
      >
        {/* Column 1: Summary */}
        <Box
          sx={{
            height: { xs: "auto", md: "100%" },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            py: 2,
            flex: { xs: "none", md: 1 },
            minWidth: 0,
            width: { xs: "100%", md: "auto" },
            overflowY: { xs: "visible", md: "auto" },
            overflowX: "hidden"
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2
            }}
          >
            <Typography>Employee Information</Typography>

            {/* employee and sp select */}
            <EmployeeAndSpSelect
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              TextFieldProps={{ required: true, "data-testmation": "employeeSelect" } as any}
              disabled={isEdit}
              selectedEmployee={selectedEmployee}
              onChange={(emp) => {
                setSelectedEmployee(emp);
              }}
            />

            <SelectedEmployee employee={selectedEmployee} />
          </Box>

          <TextField
            select
            label="Fund Type"
            value={retirementFundType}
            onChange={(e) =>
              setRetirementFundType(
                e.target.value as Exclude<
                  TRetirementFundType,
                  typeof RetirementFundType.CITIZEN_INVESTMENT_TRUST
                >
              )
            }
            variant="outlined"
            fullWidth
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={rcUsesSocialSecurityFund || rcUsesProvidentFund}
            data-testmation="fundType"
          >
            <MenuItem value={RetirementFundType.NONE} data-testmation="fundType-none">
              None
            </MenuItem>
            <MenuItem value={RetirementFundType.PROVIDENT_FUND} data-testmation="fundType-pf">
              Provident Fund
            </MenuItem>
            <MenuItem
              value={RetirementFundType.SOCIAL_SECURITY_FUND}
              data-testmation="fundType-ssf"
            >
              Social Security Fund
            </MenuItem>
          </TextField>

          {/* Salary Month and Salary Year in the same row */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Salary Year (BS)"
                value={payrollYear}
                onChange={(e) => setPayrollYear(Number(e.target.value))}
                variant="outlined"
                fullWidth
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
              >
                {nepaliYears.map((year) => (
                  <MenuItem key={year} value={year} disabled={year > currentBsYear}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Salary Month (BS)"
                value={payrollMonth}
                onChange={(e) => setPayrollMonth(Number(e.target.value))}
                variant="outlined"
                fullWidth
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
              >
                {nepaliMonths.map((mth) => (
                  <MenuItem
                    key={mth.month}
                    value={mth.month}
                    disabled={
                      payrollYear > currentBsYear ||
                      (payrollYear === currentBsYear && mth.month > currentBsMonth)
                    }
                  >
                    {mth.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* calendar summary */}
          {Object.keys(calendarSummary).length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2
              }}
            >
              <Typography>Calendar Summary</Typography>
              <Grid container spacing={2}>
                {Object.keys(calendarSummary).map((cal) => (
                  <Grid key={cal} size={{ xs: 12, sm: 6 }}>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 0.5
                        }}
                      >
                        <Typography variant="body2">
                          {calendarSummaryExtraData[cal as keyof TCalendarSummary].label}
                        </Typography>

                        <Tooltip
                          title={calendarSummaryExtraData[cal as keyof TCalendarSummary].info}
                        >
                          <InfoIcon sx={{ fontSize: 16, cursor: "help", color: "#676766" }} />
                        </Tooltip>
                      </Box>

                      <TextField
                        name={cal}
                        type="number"
                        variant="outlined"
                        fullWidth
                        value={calendarSummary[cal as keyof TCalendarSummary] || 0}
                        required={calendarSummaryExtraData[cal as keyof TCalendarSummary].required}
                        onFocus={(e) => e.target.select()}
                        disabled={attendanceSource === PayrollAttendanceSource.SYSTEM}
                        onChange={(event) => {
                          setCalendarSummary({
                            ...calendarSummary,
                            [cal]: event.target.value
                          });
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* leave summary */}
          {Object.keys(leaveSummary).length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2
              }}
            >
              <Typography>Leave Summary</Typography>
              <Grid container spacing={2}>
                {Object.keys(leaveSummary).map((lv) => (
                  <Grid key={lv} size={{ xs: 12, sm: 6 }}>
                    <TextField
                      name={lv}
                      type="number"
                      variant="outlined"
                      fullWidth
                      value={leaveSummary[lv] || 0}
                      label={lv
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                      slotProps={{
                        inputLabel: {
                          shrink: true
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                      disabled
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>

        {/* Column 2: Form */}
        <Divider
          orientation={isMobile ? "horizontal" : "vertical"}
          flexItem
          sx={{
            borderColor: "divider"
          }}
        />

        <Box
          sx={{
            height: { xs: "auto", md: "100%" },
            flex: { xs: "none", md: 1 },
            minWidth: 0,
            width: { xs: "100%", md: "auto" },
            overflowY: { xs: "visible", md: "auto" },
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            py: 2
          }}
        >
          {/* basic salary */}
          <Box>
            <Typography sx={{ mb: 2 }}>Basic Salary</Typography>
            <TextField
              type="number"
              variant="outlined"
              fullWidth
              value={basicSalary}
              slotProps={{
                inputLabel: {
                  shrink: true
                }
              }}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                setBasicSalary(Number.isNaN(value) ? 0 : value);
              }}
              disabled
            />
          </Box>

          {/* monthly allowances */}
          <Box>
            <Typography sx={{ mb: 2 }}>
              Allowances
              <Tooltip
                placement="right-start"
                title={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                      Allowances Explained:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                      <li>
                        <strong>Dearness:</strong> Offsets inflation.
                      </li>
                      <li>
                        <strong>HRA:</strong> Helps pay rent.
                      </li>
                      <li>
                        <strong>Conveyance:</strong> Covers travel to work.
                      </li>
                    </ul>
                  </Box>
                }
              >
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Typography>
            <Grid container spacing={2}>
              {AllMonthlyAllowanceComponents.map((field) => (
                <Grid key={field} size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name={field}
                    type="number"
                    variant="outlined"
                    fullWidth
                    value={allowances[field]?.value || 0}
                    label={field
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);

                      setAllowances((prev) => ({
                        ...prev,
                        [field]: {
                          value: Number.isNaN(value) ? 0 : value
                        }
                      }));
                    }}
                    disabled
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* bonuses */}
          <Box>
            <Typography sx={{ mb: 2 }}>Bonuses</Typography>
            <Grid container spacing={2}>
              {AllBonuseFields.map((field) => (
                <Grid key={field} size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name={field}
                    type="number"
                    variant="outlined"
                    fullWidth
                    value={bonuses[field]?.value || 0}
                    label={field
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);

                      setBonuses((prev) => ({
                        ...prev,
                        [field]: {
                          value: Number.isNaN(value) ? 0 : value
                        }
                      }));
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* deductions */}
          <Box>
            <Typography sx={{ mb: 2 }}>Deductions</Typography>
            <Grid container spacing={2}>
              {AllDeductionsComponents.map((field) => (
                <Grid key={field} size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name={field}
                    type="number"
                    variant="outlined"
                    fullWidth
                    value={deductions[field]?.value || 0}
                    label={field
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);

                      setDeductions((prev) => ({
                        ...prev,
                        [field]: {
                          value: Number.isNaN(value) ? 0 : value
                        }
                      }));
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Typography sx={{ mb: 2 }}>Reimbursements</Typography>

            <Grid container spacing={2}>
              {Object.entries(reimbursements).map(([key, reimbursement]) => (
                <Grid key={key} size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    onFocus={(e) => e.target.select()}
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    type="number"
                    value={reimbursement.value || 0}
                    onChange={(e) =>
                      setReimbursements((prev) => ({
                        ...prev,
                        [key]: {
                          value: Number(e.target.value)
                        }
                      }))
                    }
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* tax adjustment */}
          <Box>
            <Typography sx={{ mb: 2 }}>
              Tax Adjustment
              <Tooltip
                placement="right-start"
                title={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                      Tax Adjustment:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                      <li>
                        <strong>Use positive value to add the tax.</strong>
                      </li>
                      <li>
                        <strong>Use negative value to subtract the tax.</strong>
                      </li>
                    </ul>
                  </Box>
                }
              >
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Typography>

            <TextField
              label="Tax Adjustment"
              placeholder="Any tax adjustment to be made for the month"
              variant="outlined"
              type="number"
              value={taxAdjustment}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                setTaxAdjustment(Number.isNaN(value) ? 0 : value);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
        </Box>

        <Divider
          orientation={isMobile ? "horizontal" : "vertical"}
          flexItem
          sx={{
            borderColor: "divider"
          }}
        />

        {/* Column 3: info */}
        <Box
          sx={{
            height: { xs: "auto", md: "100%" },
            overflowY: { xs: "visible", md: "auto" },
            overflowX: "hidden",
            flex: { xs: "none", md: 1 },
            minWidth: 0,
            width: { xs: "100%", md: "auto" },
            py: 2
          }}
        >
          {/* salary breakdown */}
          <SalaryBreakdown
            bonuses={bonuses}
            deductions={deductions}
            salarySummary={salarySummary}
            fundType={retirementFundType}
            reimbursements={reimbursements}
          />
        </Box>
      </Box>
    </Panel>
  );
}
